<template>
  <div class="hh-wrap">
    <table class="hh">
      <thead>
        <tr>
          <th class="hh-name">{{ $t('s4c-utilization.heatmap.hero') }}</th>
          <th v-for="d in days" :key="d.date" :class="{ hol: d.holiday }">
            <span class="dow">{{ dow(d.date) }}</span>
            <span class="dom">{{ dom(d.date) }}</span>
          </th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="hero in roster" :key="hero.id">
          <td class="hh-name" :title="hero.name">{{ hero.name }}</td>
          <td
            v-for="d in days"
            :key="d.date"
            :class="cellClass(cell(d, hero.id), d)"
            :title="tooltip(cell(d, hero.id), d, hero)"
          >
            {{ cellLabel(cell(d, hero.id), d) }}
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script>
/**
 * Utilization per hero and day.
 *
 * Four states have to be distinguishable at a glance, and each of them was a bug
 * before it was a rule:
 *
 * - **absent** — a full-day absence leaves capacity at zero. Rendering that as
 *   "0%" reads as an idle hero; it is an unavailable one, so it gets its own
 *   marker and wash.
 * - **capped** — `booked` is capped at one day per Einsatz (S4C's own rule), so a
 *   cell can read exactly 100 while the underlying plan was longer. Those show
 *   `100+` and put the true load in the tooltip, otherwise ~17% of cells lie.
 * - **over** — booked beyond a reduced capacity (e.g. work on a half-absence
 *   day). Red rather than a darker blue: it is a planning fault, not more of the
 *   same good thing.
 * - **holiday** — no roster-wide capacity at all; hatched, never 0%.
 *
 * The fill is a single-hue sequential ramp (magnitude, so one hue light->dark),
 * defined in CSS as `.s0`..`.s12`; this constant only fixes how many steps that
 * ramp has.
 */
const RAMP_STEPS = 13;

export default {
  name: 'HeroHeatmap',
  props: {
    days: { type: Array, required: true },
    roster: { type: Array, required: true },
  },
  methods: {
    cell(day, heroId) {
      return day.cells.find((c) => c.hero_id === heroId) || null;
    },
    dow(iso) {
      return ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'][
        (new Date(iso).getDay() + 6) % 7
      ];
    },
    dom(iso) {
      const d = new Date(iso);
      return `${d.getDate()}.${d.getMonth() + 1}.`;
    },
    step(util) {
      if (util === null || util === undefined) return 0;
      const i = Math.round(Math.min(1, Math.max(0, util)) * (RAMP_STEPS - 1));
      return Math.min(RAMP_STEPS - 1, i);
    },
    cellClass(c, day) {
      if (day.holiday) return 'is-holiday';
      if (!c) return 'is-empty';
      // Absence is checked BEFORE zero capacity: a fully absent hero also has
      // cap === 0, and testing capacity first blanks ~23% of the grid.
      if (c.absent >= 480) return 'is-absent';
      if (c.cap <= 0) return c.booked > 0 ? 'is-over' : 'is-empty';
      if (c.over > 0) return 'is-over';
      return `s${this.step(c.util)}`;
    },
    cellLabel(c, day) {
      if (day.holiday) return '';
      if (!c) return '';
      if (c.absent >= 480) return 'A';
      if (c.cap <= 0) return c.booked > 0 ? this.h(c.booked) : '';
      if (c.util === null) return '';
      const pct = Math.round(c.util * 100);
      return c.capped ? `${pct}+` : `${pct}`;
    },
    h(minutes) {
      return `${(minutes / 60).toFixed(1)}h`;
    },
    tooltip(c, day, hero) {
      if (day.holiday) {
        return `${hero.name} · ${day.date} · ${this.$t(
          's4c-utilization.heatmap.holiday',
        )}`;
      }
      if (!c) return `${hero.name} · ${day.date}`;
      const parts = [
        `${hero.name} · ${day.date}`,
        `${this.$t('s4c-utilization.heatmap.booked')}: ${this.h(c.booked)}`,
        `${this.$t('s4c-utilization.heatmap.capacity')}: ${this.h(c.cap)}`,
        `${this.$t('s4c-utilization.heatmap.jobs')}: ${c.jobs}`,
      ];
      if (c.capped) {
        parts.push(
          `${this.$t('s4c-utilization.heatmap.real-load')}: ${this.h(c.raw)}`,
        );
      }
      if (c.absent > 0) {
        parts.push(
          `${this.$t('s4c-utilization.heatmap.absent')}: ${this.h(c.absent)}`,
        );
      }
      if (c.over > 0) {
        parts.push(
          `${this.$t('s4c-utilization.heatmap.over')}: ${this.h(c.over)}`,
        );
      }
      return parts.join('\n');
    },
  },
};
</script>

<style lang="scss" scoped>
.hh-wrap {
  overflow-x: auto;
  border: 1px solid var(--s4c-border);
  border-radius: 6px;
  background: var(--s4c-surface-1);
}

.hh {
  border-collapse: separate;
  border-spacing: 2px;
  font-size: 11px;
  width: 100%;

  th {
    font-weight: 600;
    color: var(--s4c-ink-3);
    text-align: center;
    padding: 3px 2px;
    white-space: nowrap;

    .dow {
      display: block;
      font-size: 10px;
    }
    .dom {
      display: block;
      font-size: 10px;
      color: var(--s4c-ink-3);
    }
    &.hol {
      opacity: 0.5;
    }
  }

  td {
    text-align: center;
    padding: 4px 2px;
    min-width: 34px;
    border-radius: 3px;
    color: var(--s4c-ink-1);
    font-variant-numeric: tabular-nums;
  }

  .hh-name {
    text-align: left;
    max-width: 190px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: var(--s4c-ink-2);
    background: transparent;
    position: sticky;
    left: 0;
    z-index: 1;
    padding-right: 8px;
  }
  thead .hh-name {
    background: var(--s4c-surface-1);
  }
  tbody .hh-name {
    background: var(--s4c-surface-1);
  }

  .is-empty {
    background: var(--s4c-surface-2);
  }
  .is-absent {
    background: #fdf0e6;
    color: #a4521f;
    font-weight: 600;
  }
  .is-over {
    background: #fbe9e9;
    color: #a32b2b;
    font-weight: 600;
  }
  .is-holiday {
    background: repeating-linear-gradient(
      45deg,
      var(--s4c-surface-2),
      var(--s4c-surface-2) 3px,
      transparent 3px,
      transparent 6px
    );
  }

  .s0 {
    background: #f2f7fd;
  }
  .s1 {
    background: #e6effa;
  }
  .s2 {
    background: #d9e7f8;
  }
  .s3 {
    background: #c7ddf6;
  }
  .s4 {
    background: #aecdf1;
  }
  .s5 {
    background: #94bdec;
  }
  .s6 {
    background: #7aade7;
  }
  .s7 {
    background: #619ce1;
  }
  .s8 {
    background: #4a8bd9;
    color: #fff;
  }
  .s9 {
    background: #3a7ac9;
    color: #fff;
  }
  .s10 {
    background: #2d69b4;
    color: #fff;
  }
  .s11 {
    background: #22589c;
    color: #fff;
  }
  .s12 {
    background: #194784;
    color: #fff;
  }
}
</style>
